import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { navigate, useParams } from '@reach/router'
import { useDispatch } from 'react-redux'
import { Grid, InputLabel, Button as MuiButton, Tooltip } from '@material-ui/core'
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined'
import { Trash } from '@refera/ui-icons'
import HeaderTitle from '_/components/header-title'
import { BasicInput, Checkbox, ControlledSwitch, MoneyInput } from '_/components/inputs'
import { Loader, Button } from '@refera/ui-web'
import ActionButton from '_/components/button'

import { useToast } from '_/hooks/use-toast'
import { useDialog } from '_/hooks/use-dialog'
import { getLaborTypeById, patchLaborType, postLaborType } from '_/modules/labor-type/actions'
import { ROUTES } from '_/utils/constants'
import useNavbarButtonsStyles from '_/views/agency-user/navbar-buttons/styles'

import useStyles from './styles'

const PRIMARY_FIELDS = ['name', 'slug', 'paintingAppliedPercentage']
const IDEAL_FIELDS = ['dailyIdeal', 'periodIdeal', 'hourIdeal']
const MAX_FIELDS = ['dailyMax', 'periodMax', 'hourMax']
const NUMERIC_FIELDS = [...IDEAL_FIELDS, ...MAX_FIELDS, 'paintingAppliedPercentage']
const REQUIRED_FIELDS = ['name', 'slug']
const LABOR_BY_AREA_FLAG_FIELD = 'laborTimeBySquareMeter'
const LABOR_BY_AREA_RANGES_FIELD = 'laborTimeBySquareMeterRanges'
const EMPTY_LABOR_RANGE = {
  startSquareMeter: null,
  endSquareMeter: null,
  laborTimeHours: null,
}

const EMPTY_LABOR_TYPE = {
  name: '',
  slug: '',
  paintingAppliedPercentage: '',
  dailyIdeal: '',
  periodIdeal: '',
  hourIdeal: '',
  dailyMax: '',
  periodMax: '',
  hourMax: '',
  laborTimeBySquareMeter: false,
  laborTimeBySquareMeterRanges: [],
}

const FIELD_LABELS = {
  id: 'ID',
  name: 'Nome',
  slug: 'Slug',
  paintingAppliedPercentage: '% com pintura',
  dailyIdeal: 'Diária ideal',
  periodIdeal: 'Período ideal',
  hourIdeal: 'Hora ideal',
  dailyMax: 'Diária máxima',
  periodMax: 'Período máximo',
  hourMax: 'Hora máxima',
  laborTimeBySquareMeter: 'MO por m²',
  laborTimeBySquareMeterRanges: 'Faixas de tempo de MO por m²',
  startSquareMeter: 'Início m²',
  endSquareMeter: 'Fim m²',
  laborTimeHours: 'Tempo MO (h)',
}

const isObjectValue = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)

const isStructuredValue = value => Array.isArray(value) || isObjectValue(value)

const formatFieldLabel = key => {
  if (FIELD_LABELS[key]) {
    return FIELD_LABELS[key]
  }

  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^\w/, char => char.toUpperCase())
}

const formatInitialValue = value => {
  if (isStructuredValue(value)) {
    return JSON.stringify(value, null, 2)
  }

  if (value == null) {
    return ''
  }

  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return String(value).replace('.', ',')
  }

  return String(value)
}

const formatFormValues = laborType =>
  Object.entries(laborType ?? {}).reduce((acc, [key, value]) => {
    if (key === LABOR_BY_AREA_RANGES_FIELD) {
      return {
        ...acc,
        [key]: parseLaborTimeRanges(value),
      }
    }

    if (key === 'paintingAppliedPercentage') {
      return {
        ...acc,
        [key]: formatPercentageToDisplay(value),
      }
    }

    return {
      ...acc,
      [key]: formatInitialValue(value),
    }
  }, {})

const parseNumberValue = value => {
  if (value === '' || value == null) {
    return null
  }

  if (typeof value === 'number') {
    return value
  }

  let normalizedValue = String(value).trim()

  if (!normalizedValue) {
    return null
  }

  normalizedValue = normalizedValue.replace(/[^\d,.-]/g, '')

  if (normalizedValue.includes(',') && normalizedValue.includes('.')) {
    normalizedValue = normalizedValue.replace(/\./g, '').replace(',', '.')
  } else if (normalizedValue.includes(',')) {
    normalizedValue = normalizedValue.replace(',', '.')
  }

  const parsedValue = Number(normalizedValue)

  return Number.isNaN(parsedValue) ? value : parsedValue
}

const formatPercentageToDisplay = value => {
  const parsedValue = parseNumberValue(value)

  return typeof parsedValue === 'number' && !Number.isNaN(parsedValue)
    ? String(parsedValue * 100).replace('.', ',')
    : ''
}

const decimalPattern = /^\d+([.,]\d{1,2})?$/

const parseLaborTimeRange = range => ({
  startSquareMeter: parseNumberValue(range?.startSquareMeter),
  endSquareMeter: parseNumberValue(range?.endSquareMeter),
  laborTimeHours: parseNumberValue(range?.laborTimeHours),
})

const parseLaborTimeRanges = ranges => {
  if (!Array.isArray(ranges)) {
    return []
  }

  return ranges.map(parseLaborTimeRange).filter(range => {
    return (
      range.startSquareMeter !== null ||
      range.endSquareMeter !== null ||
      range.laborTimeHours !== null
    )
  })
}

const isPaintingLaborType = laborType => {
  const normalizedName = String(laborType?.name ?? '')
    .trim()
    .toLowerCase()
  const normalizedSlug = String(laborType?.slug ?? '')
    .trim()
    .toLowerCase()

  return normalizedName === 'pintura' || normalizedSlug === 'painting'
}

const normalizeLaborType = laborType => {
  const nextLaborType = {
    ...EMPTY_LABOR_TYPE,
    ...(laborType ?? {}),
  }

  if (typeof laborType?.[LABOR_BY_AREA_FLAG_FIELD] !== 'boolean') {
    nextLaborType[LABOR_BY_AREA_FLAG_FIELD] = isPaintingLaborType(laborType)
  }

  nextLaborType[LABOR_BY_AREA_RANGES_FIELD] = parseLaborTimeRanges(
    laborType?.[LABOR_BY_AREA_RANGES_FIELD]
  )

  return nextLaborType
}

const parseFormValue = (key, originalValue, currentValue) => {
  if (key === LABOR_BY_AREA_RANGES_FIELD) {
    return parseLaborTimeRanges(currentValue)
  }

  if (key === 'paintingAppliedPercentage') {
    const parsedPercentage = parseNumberValue(currentValue)

    if (parsedPercentage === null || parsedPercentage === '') {
      return null
    }

    if (typeof parsedPercentage !== 'number' || Number.isNaN(parsedPercentage)) {
      return parsedPercentage
    }

    return parsedPercentage / 100
  }

  if (NUMERIC_FIELDS.includes(key)) {
    return parseNumberValue(currentValue)
  }

  if (typeof originalValue === 'boolean') {
    return Boolean(currentValue)
  }

  if (typeof originalValue === 'number') {
    return parseNumberValue(currentValue)
  }

  if (isStructuredValue(originalValue)) {
    if (isStructuredValue(currentValue)) {
      return currentValue
    }

    if (!currentValue) {
      return Array.isArray(originalValue) ? [] : {}
    }

    try {
      return JSON.parse(currentValue)
    } catch {
      return originalValue
    }
  }

  return currentValue
}

const validateJsonValue = value => {
  if (!value) {
    return true
  }

  try {
    JSON.parse(value)
    return true
  } catch {
    return 'JSON inválido'
  }
}

export default function LaborTypeDetails() {
  const styles = useStyles()
  const navbarButtonsStyles = useNavbarButtonsStyles()
  const dispatch = useDispatch()
  const { id } = useParams()
  const { showToast } = useToast()
  const { showDialog, closeDialog } = useDialog()
  const isCreating = !id

  const [isLoading, setIsLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(isCreating)
  const [laborType, setLaborType] = useState(null)

  const methods = useForm({ mode: 'all' })
  const { control, handleSubmit, reset, watch } = methods
  const laborTimeBySquareMeterEnabled = watch(LABOR_BY_AREA_FLAG_FIELD)
  const {
    fields: laborTimeRangeFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: LABOR_BY_AREA_RANGES_FIELD,
  })

  const allKeys = useMemo(() => Object.keys(laborType ?? {}), [laborType])

  const title = useMemo(() => (isCreating ? 'Novo tipo de MO' : 'Tipo de MO'), [isCreating])

  const syncLaborTypeLocally = useCallback(
    nextLaborType => {
      const normalizedLaborType = normalizeLaborType(nextLaborType)
      setLaborType(normalizedLaborType)
      reset(formatFormValues(normalizedLaborType))
    },
    [reset]
  )

  const fetchLaborType = useCallback(async () => {
    if (!id) {
      syncLaborTypeLocally(EMPTY_LABOR_TYPE)
      setIsEditing(true)
      return
    }

    setIsLoading(true)

    try {
      const data = await dispatch(getLaborTypeById(id))
      syncLaborTypeLocally(data)
    } catch (error) {
      showToast({
        type: 'error',
        message: error?.error_message ?? 'Não foi possível carregar o tipo de MO',
      })
      navigate(ROUTES.SERVICES_TYPE_LIST)
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, id, showToast, syncLaborTypeLocally])

  useEffect(() => {
    fetchLaborType()
  }, [fetchLaborType])

  const handleBackNavigation = useCallback(() => {
    navigate(ROUTES.SERVICES_TYPE_LIST)
  }, [])

  const handleCancelEdition = useCallback(() => {
    if (isCreating) {
      navigate(ROUTES.SERVICES_TYPE_LIST)
      return
    }

    reset(formatFormValues(laborType))
    setIsEditing(false)
  }, [isCreating, laborType, reset])

  const validateRequiredDecimal = useCallback(value => {
    if (value === '' || value == null) {
      return 'Esse campo é obrigatório'
    }

    return decimalPattern.test(String(value).trim())
      ? true
      : 'Informe um número decimal com até 2 casas'
  }, [])

  const validatePaintingPercentage = useCallback(value => {
    if (value === '' || value == null) {
      return true
    }

    if (!decimalPattern.test(String(value).trim())) {
      return 'Informe um percentual com até 2 casas'
    }

    const parsedValue = parseNumberValue(value)

    if (typeof parsedValue !== 'number' || Number.isNaN(parsedValue)) {
      return 'Informe um percentual válido'
    }

    if (parsedValue < 0) {
      return 'Não é permitido valor negativo'
    }

    if (parsedValue > 100) {
      return 'Informe um percentual de até 100'
    }

    return true
  }, [])

  const handleAddLaborTimeRange = useCallback(() => {
    append(EMPTY_LABOR_RANGE)
  }, [append])

  const confirmDialog = useCallback(() => {
    return new Promise(resolve => {
      showDialog({
        type: 'warning',
        subject: 'Tem certeza que deseja desativar o cálculo por m²?',
        description:
          'Ao continuar, todos os serviços vinculados a este tipo de MO deixarão de utilizar o tempo de execução baseado na metragem quadrada.',
        labelApprove: 'Continuar',
        labelCancel: 'Cancelar',
        onApprove: () => {
          closeDialog()
          resolve(true)
        },
        onCancel: () => {
          closeDialog()
          resolve(false)
        },
      })
    })
  }, [showDialog, closeDialog])

  const handleSave = useCallback(
    async formData => {
      if (!laborType) {
        return
      }

      const wasEnabled = Boolean(laborType?.[LABOR_BY_AREA_FLAG_FIELD])
      const isNowDisabled = !formData?.laborTimeBySquareMeter
      if (wasEnabled && isNowDisabled) {
        const approved = await confirmDialog()

        if (!approved) {
          return
        }
      }

      const shouldSetLaborTimeBySquareMeter = Boolean(formData[LABOR_BY_AREA_FLAG_FIELD])
      const parsedRanges = parseLaborTimeRanges(formData[LABOR_BY_AREA_RANGES_FIELD])
      const normalizedFormData = {
        ...formData,
        [LABOR_BY_AREA_RANGES_FIELD]: shouldSetLaborTimeBySquareMeter
          ? formData[LABOR_BY_AREA_RANGES_FIELD]
          : [],
      }

      if (shouldSetLaborTimeBySquareMeter && !parsedRanges.length) {
        showToast({
          type: 'error',
          message:
            'Adicione ao menos uma faixa de m² para definir o tempo de mão de obra por metragem.',
        })
        return
      }

      const payload = Object.entries(laborType).reduce((acc, [key, value]) => {
        if (
          !isCreating &&
          (key === 'id' || key === 'slug' || key === 'name' || key.endsWith('At'))
        ) {
          return acc
        }

        return {
          ...acc,
          [key]: parseFormValue(key, value, normalizedFormData[key]),
        }
      }, {})

      setIsLoading(true)

      try {
        if (isCreating) {
          const data = await dispatch(postLaborType(payload))
          showToast({
            type: 'success',
            message: 'Tipo de MO criado com sucesso',
          })
          navigate(`${ROUTES.SERVICES_TYPE_LIST}/${data?.id}`)
          return
        }

        const data = await dispatch(patchLaborType(id, payload))
        const nextData = data?.id ? data : await dispatch(getLaborTypeById(id))

        syncLaborTypeLocally(nextData)
        setIsEditing(false)
        showToast({
          type: 'success',
          message: 'Tipo de MO atualizado com sucesso',
        })
      } catch (error) {
        showToast({
          type: 'error',
          message:
            error?.error_message ??
            (isCreating
              ? 'Não foi possível criar o tipo de MO'
              : 'Não foi possível atualizar o tipo de MO'),
        })
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, id, isCreating, laborType, showToast, syncLaborTypeLocally]
  )

  const renderField = useCallback(
    key => {
      if (!laborType) {
        return null
      }

      const isMainKnownField = [...PRIMARY_FIELDS, ...IDEAL_FIELDS, ...MAX_FIELDS].includes(key)
      const hasFieldValue = allKeys.includes(key)

      if (!hasFieldValue && !isMainKnownField) {
        return null
      }

      const value = hasFieldValue ? laborType[key] : EMPTY_LABOR_TYPE[key]
      const disabled =
        !isEditing ||
        (!isCreating && (key === 'id' || key === 'slug' || key === 'name' || key.endsWith('At')))
      const label =
        key === 'paintingAppliedPercentage' ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>{formatFieldLabel(key)}</span>
            <Tooltip title="Percentual de mão de obra de serviço com pintura" arrow>
              <HelpOutlineOutlinedIcon style={{ marginLeft: 4, fontSize: 18 }} />
            </Tooltip>
          </div>
        ) : (
          formatFieldLabel(key)
        )

      if (key === 'paintingAppliedPercentage') {
        const percentageDefaultValue = formatPercentageToDisplay(value)

        return (
          <Grid key={key}>
            <BasicInput
              name={key}
              label={label}
              labelClasses={styles.inputLabel}
              defaultValue={percentageDefaultValue}
              disabled={disabled}
              rules={{ validate: validatePaintingPercentage }}
            />
          </Grid>
        )
      }

      if (IDEAL_FIELDS.includes(key) || MAX_FIELDS.includes(key)) {
        return (
          <Grid key={key}>
            <MoneyInput
              name={key}
              label={label}
              labelClasses={styles.inputLabel}
              defaultValue={value == null ? '' : value}
              disabled={disabled}
              rules={{ min: { value: 0, message: 'Não é permitido valor negativo' } }}
            />
          </Grid>
        )
      }

      if (typeof value === 'boolean') {
        return (
          <Grid key={key} className={styles.checkboxField}>
            <Checkbox name={key} label={label} defaultValue={value} disabled={disabled} />
          </Grid>
        )
      }

      if (isStructuredValue(value)) {
        return (
          <Grid key={key} className={styles.fullWidth}>
            <BasicInput
              name={key}
              label={label}
              labelClasses={styles.inputLabel}
              defaultValue={formatInitialValue(value)}
              disabled={disabled}
              rules={{ validate: validateJsonValue }}
              textArea
              multiline
              minRows={4}
              maxRows={12}
            />
          </Grid>
        )
      }

      return (
        <Grid key={key}>
          <BasicInput
            name={key}
            label={label}
            labelClasses={styles.inputLabel}
            defaultValue={formatInitialValue(value)}
            required={REQUIRED_FIELDS.includes(key)}
            rules={
              REQUIRED_FIELDS.includes(key)
                ? { required: { value: true, message: 'Esse campo é obrigatório' } }
                : {}
            }
            disabled={disabled}
          />
        </Grid>
      )
    },
    [
      allKeys,
      isCreating,
      isEditing,
      laborType,
      styles.fullWidth,
      styles.inputLabel,
      validatePaintingPercentage,
    ]
  )

  return (
    <Grid className={styles.container}>
      <Loader open={isLoading} hasBackdrop />

      <HeaderTitle title={title} backButtonAction={handleBackNavigation}>
        <div className={navbarButtonsStyles.container}>
          {isEditing ? (
            <>
              <MuiButton
                disabled={isLoading}
                className={navbarButtonsStyles.buttonRed}
                onClick={handleCancelEdition}
              >
                Cancelar
              </MuiButton>
              <MuiButton
                disabled={isLoading}
                className={navbarButtonsStyles.buttonSave}
                form="laborTypeForm"
                type="submit"
              >
                Salvar
              </MuiButton>
            </>
          ) : (
            <ActionButton
              id="edit-button"
              onClick={() => setIsEditing(true)}
              className={navbarButtonsStyles.buttons}
              variant="outlined"
              type="button"
              disabled={!laborType || isCreating}
            >
              Editar
            </ActionButton>
          )}
        </div>
      </HeaderTitle>

      <FormProvider {...methods}>
        <form id="laborTypeForm" onSubmit={handleSubmit(handleSave)}>
          <Grid className={styles.main}>
            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Dados principais</InputLabel>
              <div className={styles.wrapper}>{PRIMARY_FIELDS.map(renderField)}</div>
            </div>

            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Valores ideais</InputLabel>
              <div className={styles.wrapper}>{IDEAL_FIELDS.map(renderField)}</div>
            </div>

            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Valores máximos</InputLabel>
              <div className={styles.wrapper}>{MAX_FIELDS.map(renderField)}</div>
            </div>

            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Metragem</InputLabel>
              <div className={styles.wrapperFull}>
                <Grid className={styles.moSwitchWrapper}>
                  <ControlledSwitch
                    name={LABOR_BY_AREA_FLAG_FIELD}
                    label={
                      <span className={styles.moSwitchLabel}>
                        MO por m²
                        <Tooltip
                          title="Ative esta opção para que o Tempo de Mão de Obra (MO) deste tipo de mão de obra seja definido com base na metragem quadrada (m²)."
                          arrow
                        >
                          <HelpOutlineOutlinedIcon className={styles.moSwitchHelpIcon} />
                        </Tooltip>
                      </span>
                    }
                    defaultValue={Boolean(laborType?.[LABOR_BY_AREA_FLAG_FIELD])}
                    disabled={!isEditing}
                  />
                </Grid>

                {laborTimeBySquareMeterEnabled && (
                  <Grid className={styles.moRangesContainer}>
                    <Grid className={styles.moRangesHeader}>
                      <div className={styles.moRangesTitle}>
                        Faixas de valor em m² para tempo de MO
                      </div>
                      {isEditing && (
                        <Button
                          variant="secondary"
                          style={{ width: '160px' }}
                          onClick={handleAddLaborTimeRange}
                        >
                          + Adicionar linha
                        </Button>
                      )}
                    </Grid>

                    <Grid
                      className={`${styles.moRangesRows} ${
                        !isEditing ? styles.moRangesRowsReadOnly : ''
                      }`}
                    >
                      {laborTimeRangeFields.map((field, index) => (
                        <Grid
                          key={field.id}
                          className={`${styles.moRangesRow} ${
                            !isEditing ? styles.moRangesRowReadOnly : ''
                          }`}
                        >
                          <BasicInput
                            name={`${LABOR_BY_AREA_RANGES_FIELD}[${index}].startSquareMeter`}
                            label="Início m²"
                            labelClasses={styles.inputLabel}
                            defaultValue={formatInitialValue(field?.startSquareMeter)}
                            disabled={!isEditing}
                            rules={{ validate: validateRequiredDecimal }}
                            required
                          />
                          <BasicInput
                            name={`${LABOR_BY_AREA_RANGES_FIELD}[${index}].endSquareMeter`}
                            label="Fim m²"
                            labelClasses={styles.inputLabel}
                            defaultValue={formatInitialValue(field?.endSquareMeter)}
                            disabled={!isEditing}
                            rules={{ validate: validateRequiredDecimal }}
                            required
                          />
                          <BasicInput
                            name={`${LABOR_BY_AREA_RANGES_FIELD}[${index}].laborTimeHours`}
                            label="Tempo MO (h)"
                            labelClasses={styles.inputLabel}
                            defaultValue={formatInitialValue(field?.laborTimeHours)}
                            disabled={!isEditing}
                            rules={{ validate: validateRequiredDecimal }}
                            required
                          />
                          {isEditing && (
                            <MuiButton
                              className={styles.moRemoveLineButton}
                              onClick={() => remove(index)}
                              type="button"
                            >
                              <Trash color="#C90000" fontSize={20} />
                            </MuiButton>
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}
              </div>
            </div>
          </Grid>
        </form>
      </FormProvider>
    </Grid>
  )
}
